import React, { useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch, SearchBox, RefinementList, useInfiniteHits } from "react-instantsearch";
import SearchToolbar from "../source_directory/SearchToolbar";
import FilterPill from "../source_directory/FilterPill";
import FilterClearEffect from "../source_directory/FilterClearEffect";
import sharedAlgoliaRouter from "../source_directory/sharedAlgoliaRouter";
import useSavedOnlyFilter from "../source_directory/useSavedOnlyFilter";
import DefaultIndexConfigure from "../source_directory/DefaultIndexConfigure";
import withUnfilteredDefaultFacets from "../source_directory/withUnfilteredDefaultFacets";
import ProductCardGrid from "./card/ProductCardGrid";
import ProductCardList from "./card/ProductCardList";
import productListActionColumnWidth from "./card/listActionColumn";
import { listHeaderRowStyle } from "../source_directory/utils";
import useDebouncedQueryHook from "../source_directory/useDebouncedQueryHook";
import useInfiniteScroll from "../hooks/use_infinite_scroll";
import NoResultsMessage from "../algolia_search/no_results_message";

function ProductHits({ viewMode }) {
  const { hits, isLastPage, showMore } = useInfiniteHits();
  const hasMore = !isLastPage;
  const [lastTriggeredAt, scrollTriggerRef] = useInfiniteScroll({ hasMore });

  useEffect(() => {
    if (lastTriggeredAt > 0) showMore();
  }, [lastTriggeredAt, showMore]);

  if (viewMode === "list") {
    return (
      <>
        <NoResultsMessage hits={hits} />
        {hits.length > 0 && (
          <div className="sd-list-header-row" style={listHeaderRowStyle}>
            <span aria-hidden="true" style={{ flex: "0 0 56px" }} />
            <span style={{ flex: 1 }}>Product</span>
            <span aria-hidden="true" style={{ flex: `0 0 ${productListActionColumnWidth()}px` }} />
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {hits.map((hit) => (
            <ProductCardList key={hit.objectID} hit={hit} />
          ))}
        </div>
        {hasMore && <div ref={scrollTriggerRef} />}
      </>
    );
  }

  return (
    <>
      <NoResultsMessage hits={hits} />
      <div className="sd-cards-grid">
        {hits.map((hit) => (
          <ProductCardGrid key={hit.objectID} hit={hit} />
        ))}
      </div>
      {hasMore && <div ref={scrollTriggerRef} />}
    </>
  );
}

ProductHits.propTypes = {
  viewMode: PropTypes.oneOf(["grid", "list"]).isRequired,
};

function ProductSearch({
  algoliaAppId,
  algoliaSearchKey,
  indexName,
  viewMode,
  showFilters,
  onToggleFilters,
  savedOnly,
  onToggleSavedOnly,
  onViewModeChange,
  groupCount,
  onMyGroupsClick,
}) {
  const queryHook = useDebouncedQueryHook();
  const searchClient = useMemo(
    () => withUnfilteredDefaultFacets(algoliasearch(algoliaAppId, algoliaSearchKey)),
    [algoliaAppId, algoliaSearchKey],
  );

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const hasAlgoliaParams = Array.from(currentUrl.searchParams.keys()).some((key) => key.includes(`${indexName}[`));
    const query = currentUrl.searchParams.get("query");
    const category = currentUrl.searchParams.get("category");

    if ((query || category) && !hasAlgoliaParams) {
      const newUrl = new URL(window.location.href);
      newUrl.search = "";
      if (query) newUrl.searchParams.set(`${indexName}[query]`, query);
      if (category) newUrl.searchParams.set(`${indexName}[refinementList][product_categories.name][0]`, category);
      window.history.replaceState(null, "", newUrl.toString());
    }
  }, [indexName]);

  const savedFilter = useSavedOnlyFilter(savedOnly, "Id::PromoEntity::Product");

  return (
    <div className="ais-InstantSearch">
      <InstantSearch
        indexName={indexName}
        searchClient={searchClient}
        routing={{ router: sharedAlgoliaRouter }}
        future={{ preserveSharedStateOnUnmount: true, cleanUrlOnDispose: false }}
      >
        <DefaultIndexConfigure baseFilter={savedFilter} unfilteredFacets={showFilters} />
        <FilterClearEffect showFilters={showFilters} />
        <SearchToolbar
          showFilters={showFilters}
          onToggleFilters={onToggleFilters}
          savedOnly={savedOnly}
          onToggleSavedOnly={onToggleSavedOnly}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          groupCount={groupCount}
          onMyGroupsClick={onMyGroupsClick}
        >
          <SearchBox autoFocus placeholder="Search" searchAsYouType queryHook={queryHook} />
        </SearchToolbar>

        <div
          className="sd-filter-row"
          style={{ display: showFilters ? "flex" : "none", flexWrap: "wrap", gap: 8, marginBottom: 12 }}
        >
          <FilterPill label="Category" attribute="product_categories.name">
            <RefinementList attribute="product_categories.name" limit={10} showMore searchable />
          </FilterPill>
          <FilterPill label="Company" attribute="company.name">
            <RefinementList attribute="company.name" limit={10} showMore searchable />
          </FilterPill>
        </div>

        <ProductHits viewMode={viewMode} />
      </InstantSearch>
    </div>
  );
}

ProductSearch.propTypes = {
  algoliaAppId: PropTypes.string.isRequired,
  algoliaSearchKey: PropTypes.string.isRequired,
  indexName: PropTypes.string.isRequired,
  viewMode: PropTypes.oneOf(["grid", "list"]).isRequired,
  showFilters: PropTypes.bool.isRequired,
  onToggleFilters: PropTypes.func.isRequired,
  savedOnly: PropTypes.bool,
  onToggleSavedOnly: PropTypes.func.isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  groupCount: PropTypes.number,
  onMyGroupsClick: PropTypes.func,
};

export default ProductSearch;
